import type { CSSProperties } from 'react';
import { useStore } from '@/store/useStore';
import { STAGE_LABELS, STAGE_STYLES, ALL_STAGE_KEYS } from '@/lib/constants';

/**
 * Stage/column display helpers.
 *
 * Root cause these solve (QA): a project's kanban columns may use ids that
 * differ from the built-in PT-BR keys — e.g. this workspace stores `qualified`
 * (English, 468 leads) / `proposal` / `won` with PT-BR labels. The static
 * STAGE_LABELS / STAGE_STYLES / ALL_STAGE_KEYS maps (keyed by 5 PT-BR strings)
 * then render the raw id, drop the badge colour, and break stage filtering /
 * progress bars. These helpers resolve everything against the live
 * `kanbanColumns` (the real source of truth, with id + label + colour + order),
 * falling back to the built-in maps for default stages.
 *
 * They read the store via getState(): stage metadata is stable for the
 * lifetime of a view, so a non-reactive read is fine and avoids threading
 * kanbanColumns through every leaf component.
 */

/** Resolve a stage/column id to its human display label. */
export function stageLabel(column: string | null | undefined): string {
  if (!column) return '—';
  const cols = useStore.getState().kanbanColumns;
  return cols.find((c) => c.id === column)?.label || STAGE_LABELS[column] || column;
}

/**
 * Tailwind class for the stage badge — keeps the existing semantic colours for
 * the built-in PT-BR stages (no visual change). Returns '' for custom/English
 * ids, which then fall back to stageColorStyle() below.
 */
export function stageClassName(column: string | null | undefined): string {
  return (column && STAGE_STYLES[column]) || '';
}

/**
 * Inline colour for stage ids the static STAGE_STYLES map doesn't cover
 * (custom or English column ids). Derived from the column's own kanban colour
 * so the badge matches the board. Returns {} when STAGE_STYLES already handles
 * the id (PT-BR built-ins) or no colour is configured.
 */
export function stageColorStyle(column: string | null | undefined): CSSProperties {
  if (!column || STAGE_STYLES[column]) return {};
  const col = useStore.getState().kanbanColumns.find((c) => c.id === column);
  if (!col?.color) return {};
  return {
    backgroundColor: withAlpha(col.color, 0.15),
    color: col.color,
    borderColor: withAlpha(col.color, 0.3),
  };
}

/**
 * Ordered list of the project's stage ids (kanban column ids, sorted by order).
 * Falls back to the built-in PT-BR keys when no columns are loaded. Use this
 * instead of the static ALL_STAGE_KEYS for stage filters and progress bars so
 * they match the project's actual columns (incl. English/custom ids).
 */
export function stageKeys(): string[] {
  const cols = useStore.getState().kanbanColumns;
  if (cols.length) return [...cols].sort((a, b) => a.order - b.order).map((c) => c.id);
  return ALL_STAGE_KEYS;
}

/** Add an alpha channel to a hex / hsl() / rgb() colour string. */
function withAlpha(color: string, alpha: number): string {
  const c = color.trim();
  if (c.startsWith('#')) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split('').map((x) => x + x).join('');
    if (hex.length !== 6) return c;
    const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `#${hex}${a}`;
  }
  if (c.startsWith('hsl(')) return c.replace(/^hsl\(/, 'hsla(').replace(/\)$/, `, ${alpha})`);
  if (c.startsWith('rgb(')) return c.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${alpha})`);
  return c;
}
